import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MdSchool, MdCompare, MdCode, MdMenuBook, MdArrowOutward, MdArticle, MdMenu, MdClose, MdSportsEsports, MdEmojiEvents } from 'react-icons/md';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import CollegeListSection from './CollegeListSection';

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const userName = user?.displayName || user?.email?.split('@')[0] || 'User';

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const navStyle = { background: 'linear-gradient(135deg, #9a51a0, #f36f21)' };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="mx-auto max-w-7xl px-3 py-4 sm:px-4 sm:py-6 lg:px-8 lg:py-8">

        {/* Header */}
        <div className="mb-4 rounded-[30px] border border-gray-100 bg-white p-3 shadow-sm sm:mb-6 sm:p-5 lg:mb-8 lg:p-7">
          <div className="flex items-center justify-between gap-3">

            {/* Hamburger - mobile */}
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-gray-100 sm:hidden"
            >
              <MdMenu className="h-6 w-6 text-gray-900" />
            </button>

            {/* Logo + Title */}
            <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
              <img
                src="/favicon.png"
                alt="Logo"
                loading="eager"
                fetchpriority="high"
                decoding="async"
                className="h-10 w-10 shrink-0 object-contain sm:h-14 sm:w-14 lg:h-16 lg:w-16"
              />
              <div className="min-w-0">
                <h1 className="bg-gradient-to-r from-purple-600 to-orange-500 bg-clip-text text-base font-bold leading-normal text-transparent sm:text-xl lg:text-2xl">
                  RACE Next Step
                </h1>
                <p className="text-xs font-medium text-gray-500 sm:text-sm">Welcome, {userName}</p>
              </div>
            </div>

            {/* Desktop nav */}
            <div className="hidden items-center gap-2 sm:flex">
              <Link to="/tutorials" className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium text-white shadow-sm transition-all duration-200 hover:scale-105 hover:shadow-md lg:px-4 lg:text-sm" style={navStyle}>
                <MdMenuBook className="h-3.5 w-3.5 shrink-0 lg:h-4 lg:w-4" />Tutorials
              </Link>
              <Link to="/blogs" className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium text-white shadow-sm transition-all duration-200 hover:scale-105 hover:shadow-md lg:px-4 lg:text-sm" style={navStyle}>
                <MdArticle className="h-3.5 w-3.5 shrink-0 lg:h-4 lg:w-4" />Blogs
              </Link>
              <Link to="/practice-arena" className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium text-white shadow-sm transition-all duration-200 hover:scale-105 hover:shadow-md lg:px-4 lg:text-sm" style={navStyle}>
                <MdSportsEsports className="h-3.5 w-3.5 shrink-0 lg:h-4 lg:w-4" />Practice Arena
              </Link>
              <Link to="/daily-challenge" className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium text-white shadow-sm transition-all duration-200 hover:scale-105 hover:shadow-md lg:px-4 lg:text-sm" style={navStyle}>
                <MdEmojiEvents className="h-3.5 w-3.5 shrink-0 lg:h-4 lg:w-4" />Daily Challenge
              </Link>
              <button onClick={() => navigate('/compiler', { state: { from: `${location.pathname}${location.search}` } })} className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium text-white shadow-sm transition-all duration-200 hover:scale-105 hover:shadow-md lg:px-4 lg:text-sm" style={navStyle}>
                <MdCode className="h-3.5 w-3.5 shrink-0 lg:h-4 lg:w-4" />Compiler
              </button>
              <button onClick={handleLogout} className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium text-white shadow-sm transition-all duration-200 hover:scale-105 hover:shadow-md lg:px-4 lg:text-sm" style={navStyle}>
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Drawer Backdrop */}
        <div
          className={`fixed inset-0 z-40 bg-black bg-opacity-50 transition-opacity duration-300 sm:hidden ${menuOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'}`}
          onClick={() => setMenuOpen(false)}
        />

        {/* Mobile Drawer */}
        <div className={`fixed inset-y-0 left-0 z-50 w-72 overflow-y-auto bg-white shadow-2xl transition-transform duration-300 ease-in-out sm:hidden ${menuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="flex items-center justify-between border-b border-gray-200 p-4">
            <div>
              <span className="text-lg font-bold text-gray-900">Navigation</span>
              <p className="mt-0.5 text-xs text-gray-500">Browse sections</p>
            </div>
            <button type="button" onClick={() => setMenuOpen(false)} className="rounded-lg p-2 hover:bg-gray-100">
              <MdClose className="h-6 w-6 text-gray-900" />
            </button>
          </div>
          <div className="p-4">
            <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-400">Navigation</p>
            <nav className="grid grid-cols-2 gap-2">
              <Link to="/tutorials" onClick={() => setMenuOpen(false)} className="flex items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-xs font-medium text-white shadow-md" style={navStyle}>
                <MdMenuBook className="h-3.5 w-3.5 shrink-0" />Tutorials
              </Link>
              <Link to="/blogs" onClick={() => setMenuOpen(false)} className="flex items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-xs font-medium text-white shadow-md" style={navStyle}>
                <MdArticle className="h-3.5 w-3.5 shrink-0" />Blogs
              </Link>
              <Link to="/practice-arena" onClick={() => setMenuOpen(false)} className="flex items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-xs font-medium text-white shadow-md" style={navStyle}>
                <MdSportsEsports className="h-3.5 w-3.5 shrink-0" />Practice Arena
              </Link>
              <Link to="/daily-challenge" onClick={() => setMenuOpen(false)} className="flex items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-xs font-medium text-white shadow-md" style={navStyle}>
                <MdEmojiEvents className="h-3.5 w-3.5 shrink-0" />Daily Challenge
              </Link>
              <button onClick={() => { setMenuOpen(false); navigate('/compiler', { state: { from: `${location.pathname}${location.search}` } }); }} className="flex items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-xs font-medium text-white shadow-md" style={navStyle}>
                <MdCode className="h-3.5 w-3.5 shrink-0" />Compiler
              </button>
              <button onClick={() => { setMenuOpen(false); handleLogout(); }} className="flex items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-xs font-medium text-white shadow-md" style={navStyle}>
                Logout
              </button>
            </nav>
          </div>
        </div>

        {/* Dashboard Content */}
        <div className="py-4 sm:py-8 lg:py-12">

          {/* Welcome Section */}
          <div className="mb-6 px-1 sm:mb-8">
            <div className="relative overflow-hidden rounded-[28px] border border-white/80 bg-white/95 shadow-[0_24px_70px_-42px_rgba(15,23,42,0.35)] w-full">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(154,81,160,0.14),_transparent_34%),radial-gradient(circle_at_top_right,_rgba(243,111,33,0.12),_transparent_28%)]" />
              <div className="relative px-6 py-7 sm:px-8 sm:py-8 lg:px-10 lg:py-9">
                <div className="mx-auto max-w-3xl text-center">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-[#9a51a0]">Dashboard Overview</p>
                  <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Welcome to Your Dashboard</h2>
                  <p className="mt-3 text-sm sm:text-base text-slate-600">
                    Discover colleges faster with profile viewing, side-by-side comparison, and smart browsing tools in one place.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Features Grid */}
          <div className="flex gap-3 overflow-x-auto px-1 pb-2 md:grid md:grid-cols-2 md:overflow-visible md:pb-0 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">

            <Link to="/college-profile" className="group relative shrink-0 w-[75vw] sm:w-auto overflow-hidden rounded-[18px] border border-white/80 bg-white/95 p-4 shadow-[0_18px_45px_-30px_rgba(15,23,42,0.35)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_28px_60px_-30px_rgba(154,81,160,0.28)] min-h-[150px]">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(154,81,160,0.12),_transparent_32%),radial-gradient(circle_at_bottom_right,_rgba(243,111,33,0.12),_transparent_28%)] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              <div className="relative flex h-full flex-col">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-purple-100 to-orange-100 text-[#9a51a0] shadow-sm">
                    <MdSchool className="w-5 h-5" />
                  </div>
                  <div className="h-1 w-14 rounded-full bg-gradient-to-r from-[#9a51a0] to-[#f36f21]" />
                </div>
                <div className="space-y-1.5">
                  <h3 className="text-base font-semibold text-slate-900 transition group-hover:text-[#9a51a0]">College Profile Viewer</h3>
                  <p className="text-xs leading-5 text-slate-600">Explore 3000+ colleges with search, filters, and detailed profile insights.</p>
                </div>
                <div className="mt-auto pt-3 flex items-center gap-1.5 text-xs font-semibold text-[#9a51a0]">
                  <span>Open Viewer</span>
                  <MdArrowOutward className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </div>
            </Link>

            <Link to="/comparator" className="group relative shrink-0 w-[75vw] sm:w-auto overflow-hidden rounded-[18px] border border-white/80 bg-white/95 p-4 shadow-[0_18px_45px_-30px_rgba(15,23,42,0.35)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_28px_60px_-30px_rgba(243,111,33,0.26)] min-h-[150px]">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(154,81,160,0.12),_transparent_32%),radial-gradient(circle_at_bottom_right,_rgba(243,111,33,0.12),_transparent_28%)] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              <div className="relative flex h-full flex-col">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-purple-100 to-orange-100 text-[#9a51a0] shadow-sm">
                    <MdCompare className="w-5 h-5" />
                  </div>
                  <div className="h-1 w-14 rounded-full bg-gradient-to-r from-[#9a51a0] to-[#f36f21]" />
                </div>
                <div className="space-y-1.5">
                  <h3 className="text-base font-semibold text-slate-900 transition group-hover:text-[#9a51a0]">College Comparator</h3>
                  <p className="text-xs leading-5 text-slate-600">Compare colleges side-by-side across multiple academic and decision-making parameters.</p>
                </div>
                <div className="mt-auto pt-3 flex items-center gap-1.5 text-xs font-semibold text-[#9a51a0]">
                  <span>Start Comparing</span>
                  <MdArrowOutward className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </div>
            </Link>

            <Link to="/blogs" className="group relative shrink-0 w-[75vw] sm:w-auto overflow-hidden rounded-[18px] border border-white/80 bg-white/95 p-4 shadow-[0_18px_45px_-30px_rgba(15,23,42,0.35)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_28px_60px_-30px_rgba(154,81,160,0.28)] min-h-[150px]">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(154,81,160,0.12),_transparent_32%),radial-gradient(circle_at_bottom_right,_rgba(243,111,33,0.12),_transparent_28%)] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              <div className="relative flex h-full flex-col">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-purple-100 to-orange-100 text-[#9a51a0] shadow-sm">
                    <MdArticle className="w-5 h-5" />
                  </div>
                  <div className="h-1 w-14 rounded-full bg-gradient-to-r from-[#9a51a0] to-[#f36f21]" />
                </div>
                <div className="space-y-1.5">
                  <h3 className="text-base font-semibold text-slate-900 transition group-hover:text-[#9a51a0]">Blogs Hub</h3>
                  <p className="text-xs leading-5 text-slate-600">Read student experiences, college guides, and expert articles to make better decisions.</p>
                </div>
                <div className="mt-auto pt-3 flex items-center gap-1.5 text-xs font-semibold text-[#9a51a0]">
                  <span>Explore Blogs</span>
                  <MdArrowOutward className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </div>
            </Link>

            <Link to="/tutorials" className="group relative shrink-0 w-[75vw] sm:w-auto overflow-hidden rounded-[18px] border border-white/80 bg-white/95 p-4 shadow-[0_18px_45px_-30px_rgba(15,23,42,0.35)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_28px_60px_-30px_rgba(243,111,33,0.26)] min-h-[150px]">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(154,81,160,0.12),_transparent_32%),radial-gradient(circle_at_bottom_right,_rgba(243,111,33,0.12),_transparent_28%)] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              <div className="relative flex h-full flex-col">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-purple-100 to-orange-100 text-[#9a51a0] shadow-sm">
                    <MdMenuBook className="w-5 h-5" />
                  </div>
                  <div className="h-1 w-14 rounded-full bg-gradient-to-r from-[#9a51a0] to-[#f36f21]" />
                </div>
                <div className="space-y-1.5">
                  <h3 className="text-base font-semibold text-slate-900 transition group-hover:text-[#9a51a0]">Tutorials</h3>
                  <p className="text-xs leading-5 text-slate-600">Learn coding, exam prep, and career skills with structured step-by-step tutorials.</p>
                </div>
                <div className="mt-auto pt-3 flex items-center gap-1.5 text-xs font-semibold text-[#9a51a0]">
                  <span>Start Learning</span>
                  <MdArrowOutward className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </div>
            </Link>

            <Link to="/daily-challenge" className="group relative shrink-0 w-[75vw] sm:w-auto overflow-hidden rounded-[18px] border border-white/80 bg-white/95 p-4 shadow-[0_18px_45px_-30px_rgba(15,23,42,0.35)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_28px_60px_-30px_rgba(154,81,160,0.28)] min-h-[150px]">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(154,81,160,0.12),_transparent_32%),radial-gradient(circle_at_bottom_right,_rgba(243,111,33,0.12),_transparent_28%)] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              <div className="relative flex h-full flex-col">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-purple-100 to-orange-100 text-[#9a51a0] shadow-sm">
                    <MdEmojiEvents className="w-5 h-5" />
                  </div>
                  <div className="h-1 w-14 rounded-full bg-gradient-to-r from-[#9a51a0] to-[#f36f21]" />
                </div>
                <div className="space-y-1.5">
                  <h3 className="text-base font-semibold text-slate-900 transition group-hover:text-[#9a51a0]">Daily Challenge Arena</h3>
                  <p className="text-xs leading-5 text-slate-600">One challenge every day. Compete globally, build streaks, and earn achievement badges.</p>
                </div>
                <div className="mt-auto pt-3 flex items-center gap-1.5 text-xs font-semibold text-[#9a51a0]">
                  <span>Play Today's Challenge</span>
                  <MdArrowOutward className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </div>
            </Link>

          </div>

          <CollegeListSection />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
